/**
 * QuickActions widget — keyboard-free shortcuts to the six most
 * common tenant operations.
 *
 * Migrated in Wave 3.6 from console.log stubs to real navigation via
 * react-router's useNavigate. Each tile routes to the best-matching
 * existing page in the tenant app:
 *
 *   Add Worker  → /organization/workers/active (opens list; add
 *                   flow is a dialog on that page)
 *   Schedule      → /attendance/schedules/view (ScheduleView). The router has no
 *                   bare `/attendance/schedules` parent, so the short form fell to
 *                   the catch-all and landed the admin on /dashboard.
 *   New Task      → /processes/tasks (operators assign tasks from
 *                   this surface)
 *   Attendance    → /attendance/overview
 *   New Process   → /processes/designer (create a process definition)
 *   Reports       → /attendance/reports (the cross-module reports
 *                   landing; future iterations can split out a top-level
 *                   Reports page if the vocabulary expands)
 *
 * Bug fix 2026-05-22 — actions are now filtered by the org's
 * `licensed_products`. Pre-fix, a pureoa-only org saw "Schedule",
 * "Attendance", "Reports" tiles that all 403'd on click; the user
 * would land on a license_required page or just see the dashboard
 * silently re-render. Now tiles requiring a missing product simply
 * don't render, mirroring the WorkerLayout sidebar filter pattern.
 */

import React from 'react';
import { Grid, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import {
  AccessTime, AltRoute, Assignment, EventNote, PersonAdd, Summarize,
  Inbox,
  Insights,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasDashboardProductLicense } from '../../dashboardConfig';

interface QuickAction {
  icon: React.ReactNode;
  i18nKey: string;
  path: string;
  /** When set, only render this tile if the org has the named product
   *  in its licensed_products list. Undefined = always render
   *  (organization module = shared across all licensed products). */
  requires?: 'qutime' | 'pureoa';
}

const ACTIONS: QuickAction[] = [
  { icon: <PersonAdd />, i18nKey: 'dashboard:widgets.quickActions.addWorker', path: '/organization/workers/active' },
  { icon: <EventNote />, i18nKey: 'dashboard:widgets.quickActions.schedule', path: '/attendance/schedules/view', requires: 'qutime' },
  { icon: <Assignment />, i18nKey: 'dashboard:widgets.quickActions.pendingApprovals', path: '/attendance/approvals', requires: 'qutime' },
  { icon: <AccessTime />, i18nKey: 'dashboard:widgets.quickActions.attendance', path: '/attendance/overview', requires: 'qutime' },
  { icon: <AltRoute />, i18nKey: 'dashboard:widgets.quickActions.newProcess', path: '/processes/designer', requires: 'pureoa' },
  // PureOA side of the widget. Before 2026-09-03 a pureoa-only org saw exactly TWO actions —
  // `addWorker` (unlicensed, shared) and `newProcess` — because the other four all require
  // qutime. The gating was correct; the catalogue was simply lopsided.
  //
  // These three make the two product lines symmetric rather than adding whatever fits: each
  // line now offers "act on my queue", "see everything", "design/configure" and "report",
  // with the shared organization action on top.
  //
  //   qutime : pendingApprovals · attendance    · schedule · payrollExport
  //   pureoa : processApprovals · processRequests · designer · processAnalytics
  //
  // Every path is a REAL route in App.tsx, not one of the redirect aliases: `/processes/
  // instances` and `/processes/builder` are `<Navigate>` shims kept for old deep links, and
  // pointing a quick action at a redirect would make the widget's destination a moving
  // target (svdy-ui §12.10).
  { icon: <Assignment />, i18nKey: 'dashboard:widgets.quickActions.processApprovals', path: '/processes/requests/pending', requires: 'pureoa' },
  { icon: <Inbox />, i18nKey: 'dashboard:widgets.quickActions.processRequests', path: '/processes/requests/history', requires: 'pureoa' },
  { icon: <Insights />, i18nKey: 'dashboard:widgets.quickActions.processAnalytics', path: '/processes/analytics', requires: 'pureoa' },
  { icon: <Summarize />, i18nKey: 'dashboard:widgets.quickActions.payrollExport', path: '/attendance/reports', requires: 'qutime' },
];

export interface QuickActionsProps {
  licensedProducts?: readonly string[];
}

const QuickActions: React.FC<QuickActionsProps> = ({ licensedProducts = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const visibleActions = ACTIONS.filter(
    (action) => !action.requires || hasDashboardProductLicense(licensedProducts, action.requires),
  );

  return (
    <Grid container spacing={1}>
      {visibleActions.map((action) => (
        <Grid item xs={4} key={action.i18nKey}>
          <Paper
            elevation={0}
            sx={{
              p: 0.5,
              borderRadius: 2,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 80ms ease, border-color 80ms ease',
              '&:hover': {
                borderColor: theme.palette.grey[300],
                border: '1px solid',
                bgcolor: 'grey.50',
              },
            }}
          >
            <Tooltip title={t(action.i18nKey)} arrow>
              <IconButton
                onClick={() => navigate(action.path)}
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  flexDirection: 'column',
                  gap: 0.5,
                  color: 'primary.main',
                  padding: theme.spacing(0.5),
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                {action.icon}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    textAlign: 'center',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(action.i18nKey)}
                </Typography>
              </IconButton>
            </Tooltip>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

export default QuickActions;
